import { Tooltip } from "@material-ui/core";
import { t } from "js/i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";

import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import { ShowDialog } from "../../../../components/Dialogs/BaseDialog";
import SalesFormDialog from "../../../../components/Dialogs/SalesFormDialog";
import Analytics from "js/analytics";

const SeatsSelectContainer = styled.div`
    position: relative;
    width: 62px;
`;

const DisabledStyle = css`
    color: #999;
    pointer-events: none;
`;

const SeatsSelectControl = styled.div<{ $disabled?: boolean }>`
    display: flex;
    width: 62px;
    height: 38px;
    padding: 10px;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    box-sizing: border-box;
    background: white;
    border: 1px solid #999;
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsInput = styled.input<{ $disabled?: boolean }>`
    all: unset;
    flex: 1 1 auto;
    min-width: 0;
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgb(48, 49, 61);
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsArrowButton = styled.button<{ $disabled?: boolean }>`
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    ${({ $disabled }) => $disabled && DisabledStyle}
`;

const SeatsDropdown = styled.div<{ $showOverSeatLimit?: boolean }>`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: ${({ $showOverSeatLimit }) => ($showOverSeatLimit ? "max-content" : "62px")};
    max-height: 180px;
    overflow: auto;
    border-radius: 2px;
    background: #FFF;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
    z-index: 9999;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-thumb {
        background: #999;
    }
`;

const SeatsOptionButton = styled.button<{ $active?: boolean }>`
    all: unset;
    width: 100%;
    box-sizing: border-box;
    height: 50px;
    padding: 12px 12px 12px 16px;
    cursor: pointer;
    font-family: "Source Sans Pro";
    font-size: 14px;
    line-height: 150%; /* 13.75px */
    align-self: stretch;
    color: ${({ $active }) => ($active ? "#11A9E2" : "#333")};

    &:hover {
        background: #f7fafc;
    }

`;

const DownArrowIcon = ({ color }: { color: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" fill={color} />
    </svg>
);

export const SeatsSelect = CheckoutController.withState(
    function SeatsSelect({ quantity, maxQuantity, minQuantity, disabled = false }: { disabled?: boolean } & CheckoutControllerState) {
        const seatOptions = useMemo(
            () => Array.from({ length: maxQuantity }, (_, idx) => idx + 1).filter(seat => seat >= minQuantity),
            [maxQuantity, minQuantity]
        );

        const [seatInputValue, setSeatInputValue] = useState<string>(String(quantity));
        const [seatsOpen, setSeatsOpen] = useState(false);
        const [activeSeatIndex, setActiveSeatIndex] = useState<number>(0);
        const seatsSelectRef = useRef<HTMLDivElement | null>(null);
        const seatsOptionRefs = useRef<Array<HTMLButtonElement | null>>([]);
        const seatsInputRef = useRef<HTMLInputElement | null>(null);
        const wasClickedRef = useRef(false);

        useEffect(() => {
            const currentParsed = Number.parseInt((seatInputValue ?? "").trim(), 10);
            if (currentParsed !== quantity) {
                setSeatInputValue(String(quantity));
            }
        }, [quantity]);

        useEffect(() => {
            if (!seatsOpen) {
                return;
            }

            const parsed = Number.parseInt((seatInputValue ?? "").trim(), 10);
            const parsedOptionIndex = seatOptions.findIndex(seat => seat === parsed);
            if (parsedOptionIndex >= 0) {
                setActiveSeatIndex(parsedOptionIndex);
            } else {
                setActiveSeatIndex(0);
            }
        }, [seatsOpen, seatInputValue, seatOptions]);

        useEffect(() => {
            if (!seatsOpen) {
                return;
            }

            seatsOptionRefs.current[activeSeatIndex]?.scrollIntoView({ block: "nearest" });
        }, [activeSeatIndex, seatsOpen]);

        useEffect(() => {
            const onMouseDown = (event: MouseEvent) => {
                const el = seatsSelectRef.current;
                if (!el) return;
                if (!el.contains(event.target as Node)) {
                    setSeatsOpen(false);
                }
            };

            document.addEventListener("mousedown", onMouseDown);
            return () => document.removeEventListener("mousedown", onMouseDown);
        }, []);

        const handleSeatsInputChange = (rawValue: string) => {
            const raw = rawValue.trim();
            const digitsOnly = raw.replace(/\D+/g, "");

            // Allow clearing to retype
            if (raw.length === 0) {
                setSeatInputValue("");
                return;
            }

            // If mixed input, keep only digits.
            if (digitsOnly.length === 0) {
                return;
            }

            setSeatInputValue(digitsOnly);

            const parsed = Number.parseInt(digitsOnly, 10);
            if (Number.isFinite(parsed)) {
                CheckoutController.setQuantity(parsed);

                if (parsed > maxQuantity) {
                    setSeatsOpen(true);
                }
            }
        };

        const handleContactSales = () => {
            Analytics.ModifyWorkspacePlan.EnterpriseInquiry({ workspace_id: CheckoutController.workspaceId });
            ShowDialog(SalesFormDialog, {
                onClose: () => {
                    CheckoutController.setQuantity(maxQuantity);
                },
            });
        };

        const parsedSeatInput = Number.parseInt((seatInputValue ?? "").trim(), 10);
        const isOverSeatLimit = quantity > maxQuantity;
        const showOverSeatLimit = (Number.isFinite(parsedSeatInput) ? parsedSeatInput > maxQuantity : false) || isOverSeatLimit;

        const selectSeatValue = (value: number) => {
            setSeatInputValue(String(value));
            CheckoutController.setQuantity(value);
            setSeatsOpen(false);
        };

        const openSeatsMenuSoon = () => {
            // Opening the dropdown during the focus event can interfere with mouse-based caret placement
            // in a controlled input. Deferring by a tick preserves expected cursor behavior.
            window.setTimeout(() => setSeatsOpen(true), 0);
        };

        // Handle keyboard navigation for the seats dropdown
        const handleSeatsKeyboard = (e: KeyboardEvent | React.KeyboardEvent) => {
            const key = e.key;

            // Arrow keys: navigate through options
            if (key === "ArrowDown" || key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();

                if (!seatsOpen) {
                    setSeatsOpen(true);
                    return;
                }

                if (showOverSeatLimit) {
                    return; // Can't navigate when over limit
                }

                const delta = key === "ArrowDown" ? 1 : -1;
                setActiveSeatIndex(prev => Math.max(0, Math.min(seatOptions.length - 1, prev + delta)));
                return;
            }

            // Enter: select the active option
            if (key === "Enter") {
                if (!seatsOpen) return;

                e.preventDefault();
                e.stopPropagation();

                if (showOverSeatLimit) {
                    handleContactSales();
                    setSeatsOpen(false);
                    return;
                }

                const selectedValue = seatOptions[activeSeatIndex] ?? minQuantity;
                selectSeatValue(selectedValue);
                return;
            }

            // Escape: close the dropdown
            if (key === "Escape") {
                if (!seatsOpen) return;

                e.preventDefault();
                e.stopPropagation();
                setSeatsOpen(false);
            }
        };

        // Global keyboard handler: allows navigation when dropdown is open but input isn't focused
        useEffect(() => {
            if (!seatsOpen) return;

            const onKeyDown = (event: KeyboardEvent) => {
                if (event.defaultPrevented) return;

                const activeEl = document.activeElement;
                // Skip if another input is focused (let it handle its own keyboard events)
                if (activeEl instanceof HTMLInputElement && activeEl !== seatsInputRef.current) {
                    return;
                }
                // Skip if seats input is focused (it has its own onKeyDown handler)
                if (activeEl === seatsInputRef.current) {
                    return;
                }

                handleSeatsKeyboard(event);
            };

            window.addEventListener("keydown", onKeyDown);
            return () => window.removeEventListener("keydown", onKeyDown);
        }, [seatsOpen, handleSeatsKeyboard]);

        return (
            <Tooltip title={disabled ? t("Cannot change seat count until you reactivate") : undefined}
                disableHoverListener={!disabled}
                disableFocusListener={!disabled}
            >
                <SeatsSelectContainer ref={seatsSelectRef} data-testid="seats-select">
                    <SeatsSelectControl $disabled={disabled}>
                        <SeatsInput
                            $disabled={disabled}
                            ref={seatsInputRef}
                            data-testid="seats-select-input"
                            value={seatInputValue}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            // We allow up to 2 digits for the seat count
                            // Anything above 20 will show the contact sales option
                            maxLength={2}
                            onMouseDown={() => {
                                wasClickedRef.current = true;
                            }}
                            onFocus={() => {
                                // If focus came from keyboard (tabbing), open on focus.
                                // If it came from mouse click, onClick will handle it to preserve caret placement.
                                if (!seatsOpen && !wasClickedRef.current) {
                                    openSeatsMenuSoon();
                                }
                                wasClickedRef.current = false; // Reset for next interaction
                            }}
                            onClick={() => {
                                if (!seatsOpen) {
                                    openSeatsMenuSoon();
                                }
                            }}
                            onKeyDown={handleSeatsKeyboard}
                            onChange={e => handleSeatsInputChange(e.target.value)}
                            onBlur={() => {
                                const next = (seatInputValue ?? "").trim();
                                if (!next) {
                                    setSeatInputValue(String(minQuantity));
                                    CheckoutController.setQuantity(minQuantity);
                                }
                            }}
                        />
                        <SeatsArrowButton
                            type="button"
                            $disabled={disabled}
                            onMouseDown={e => e.preventDefault()} // keep focus in input
                            onClick={() => setSeatsOpen(seatsOpen => !seatsOpen)}
                        >
                            <DownArrowIcon color={disabled ? "#999" : "#666"} />
                        </SeatsArrowButton>
                    </SeatsSelectControl>

                    {seatsOpen && (
                        <SeatsDropdown $showOverSeatLimit={showOverSeatLimit}>
                            {showOverSeatLimit ? (
                                <SeatsOptionButton
                                    type="button"
                                    $active={true}
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => {
                                        handleContactSales();
                                        setSeatsOpen(false);
                                    }}
                                >
                                    {t("For {{maxQuantity}}+ seats, contact sales", { maxQuantity })}
                                </SeatsOptionButton>
                            ) : (
                                seatOptions.map((value, index) => (
                                    <SeatsOptionButton
                                        key={value}
                                        type="button"
                                        $active={seatOptions[activeSeatIndex] === value}
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={() => {
                                            selectSeatValue(value);
                                        }}
                                        ref={el => {
                                            seatsOptionRefs.current[index] = el;
                                        }}
                                        data-testid={`seats-select-option-${value}`}
                                    >
                                        {value}
                                    </SeatsOptionButton>
                                ))
                            )}
                        </SeatsDropdown>
                    )}
                </SeatsSelectContainer>
            </Tooltip>
        );
    }
);
